import { Toast } from '@pantry/ui'
import React, { useCallback, useEffect, useState } from 'react'

type ToastVariant = 'default' | 'success' | 'error'

interface ToastState {
  open: boolean
  message: string
  variant: ToastVariant
}

// Shared toast helper for the Email-Marketing tabs. Mirrors the inline-toast
// pattern used by Category/CategoryLayout: a bottom-centered fixed viewport +
// 3s auto-dismiss. Each tab calls `useMarketingToast()` and renders
// `toastNode` at the end of its tree.
export const useMarketingToast = () => {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    variant: 'success',
  })

  useEffect(() => {
    if (!toast.open) return
    const id = window.setTimeout(
      () => setToast((prev) => ({ ...prev, open: false })),
      3000,
    )
    return () => window.clearTimeout(id)
  }, [toast.open])

  const showToast = useCallback(
    (message: string, variant: ToastVariant = 'success') =>
      setToast({ open: true, message, variant }),
    [],
  )

  const dismiss = useCallback(
    () => setToast((prev) => ({ ...prev, open: false })),
    [],
  )

  const toastNode = toast.open ? (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
      }}
    >
      <Toast
        message={toast.message}
        variant={toast.variant}
        onDismiss={dismiss}
      />
    </div>
  ) : null

  return { showToast, toastNode }
}

// Consistent error-message extraction for surfacing API failures in a toast.
// @bs/core's response interceptor rethrows the Django BadRequestException as
// either its bare `message` string or the whole `{message, code, data}` body
// (for INTERCEPTOR_ERROR_CODES) — never a `detail` key. DRF serializer
// failures arrive as a field map instead —
// `{cta_url: ['Button link must be…'], non_field_errors: ['…']}` — and are
// flattened to "cta_url: Button link must be…; …" (non_field_errors
// messages carry no prefix). Both shapes come straight from
// defaultResponseErrorHandler's `throw error.response.data` branch.
export const GENERIC_MARKETING_ERROR = 'Something went wrong. Please try again.'

const firstString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value || undefined
  if (Array.isArray(value)) {
    const first = value.find((v) => typeof v === 'string' && v)
    return typeof first === 'string' ? first : undefined
  }
  return undefined
}

// Nested serializers (e.g. `{audience: {segment_ids: […]}}`) flatten to a
// dotted path: "audience.segment_ids: …".
const collectFieldErrors = (
  errors: Record<string, unknown>,
  prefix: string,
  parts: string[],
) => {
  for (const [field, value] of Object.entries(errors)) {
    const path = prefix ? `${prefix}.${field}` : field
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      collectFieldErrors(value as Record<string, unknown>, path, parts)
      continue
    }
    const message = firstString(value)
    if (!message) continue
    // non_field_errors (serializer.validate) and stock DRF `detail`
    // (NotFound / PermissionDenied) are whole-request messages: no prefix.
    parts.push(
      field === 'non_field_errors' || path === 'detail'
        ? message
        : `${path}: ${message}`,
    )
  }
}

export const flattenFieldErrors = (
  errors: Record<string, unknown>,
): string | undefined => {
  const parts: string[] = []
  collectFieldErrors(errors, '', parts)
  return parts.length > 0 ? parts.join('; ') : undefined
}

export const getMarketingErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string' && error) return error
  // DRF's non-dict ValidationError('…') serialises as a bare string list.
  if (Array.isArray(error)) return firstString(error) ?? GENERIC_MARKETING_ERROR
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message) return message
    if (message === undefined) {
      const flattened = flattenFieldErrors(error as Record<string, unknown>)
      if (flattened) return flattened
    }
  }
  return GENERIC_MARKETING_ERROR
}
